import React, { useState, useEffect } from 'react';
import { Plus, FolderOpen, Calendar, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { toast } from 'sonner';
import LogCard from '../components/LogCard';
import LogFormModal from '../components/LogFormModal';
import { useRealtimeTables } from '../hooks/useRealtimeTables';
import { getLocalDateKey } from '../utils/dateFormatter';

const Logger = () => {
  const [logs, setLogs] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  // Advanced filtering state
  const [viewMode, setViewMode] = useState('specific_date'); // 'specific_date' or 'all_history'
  const [selectedDate, setSelectedDate] = useState(() => {
    // Default to today in YYYY-MM-DD format
    return getLocalDateKey(new Date());
  });
  const [selectedUserId, setSelectedUserId] = useState('ALL');
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [viewingLog, setViewingLog] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showDriveLinksDropdown, setShowDriveLinksDropdown] = useState(false);
  const isAdmin = userProfile?.role === 'admin';

  useEffect(() => {
    loadCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUserId) {
      loadLogs();
    }
  }, [selectedDate, currentUserId, viewMode, selectedUserId]);

  useEffect(() => {
    if (currentUserId) {
      loadUserProfile();
    }
  }, [currentUserId]);

  useEffect(() => {
    // Fetch all users if current user is admin
    if (currentUserId && userProfile?.role === 'admin') {
      loadAllUsers();
    }
  }, [currentUserId, userProfile?.role]);

  const loadCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
      }
    } catch (error) {
      console.error('Error loading current user:', error);
      toast.error('Failed to load user information');
    }
  };

  const loadUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setUserProfile(null);
        return;
      }

      // Fetch current user's profile from public.users table
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error loading user profile:', error);
        setUserProfile(null);
        return;
      }

      setUserProfile(data || null);
    } catch (error) {
      console.error('Error loading user profile:', error);
      setUserProfile(null);
    }
  };

  const loadAllUsers = async () => {
    try {
      // Fetch all users for admin dropdown
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, is_active, inactive_user_name, drive_folder_link')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error loading users list:', error);
        toast.error('Failed to load users list');
        return;
      }

      setUsersList(data || []);
    } catch (error) {
      console.error('Error loading users list:', error);
      toast.error('Failed to load users list');
    }
  };

  const loadLogs = async (options = {}) => {
    const { silent = false } = options;

    try {
      if (!silent) {
        setLoading(true);
      }
      
      // Base query with join to users table
      let query = supabase
        .from('daily_logs')
        .select('*, users(name, is_active, inactive_user_name, drive_folder_link)')
        .order('created_at', { ascending: false });

      // Non-admin users can only read their own logs.
      if (!isAdmin) {
        query = query.eq('user_id', currentUserId);
      }

      // Apply date filter only if viewMode is 'specific_date'
      if (viewMode === 'specific_date') {
        query = query.eq('log_date', selectedDate);
      }

      // Apply selected user filter for admins.
      if (isAdmin && selectedUserId && selectedUserId !== 'ALL') {
        query = query.eq('user_id', selectedUserId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error loading logs:', error);
        toast.error('Failed to load logs: ' + error.message);
        return;
      }

      setLogs(data || []);
    } catch (error) {
      console.error('Error loading logs:', error);
      toast.error('Failed to load logs: ' + error.message);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useRealtimeTables({
    channelName: 'logger-daily-logs',
    tables: ['daily_logs'],
    enabled: Boolean(currentUserId),
    onChange: () => loadLogs({ silent: true })
  });

  useRealtimeTables({
    channelName: 'logger-users',
    tables: ['users'],
    enabled: Boolean(currentUserId),
    onChange: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: refreshedProfile } = user
        ? await supabase.from('users').select('*').eq('id', user.id).maybeSingle()
        : { data: null };

      setUserProfile(refreshedProfile || null);

      if (refreshedProfile?.role === 'admin') {
        await loadAllUsers();
      }

      await loadLogs({ silent: true });
    }
  });

  const handleCreateLog = () => {
    setEditingLog(null);
    setViewingLog(null);
    setIsViewMode(false);
    setShowFormModal(true);
  };

  const handleEditLog = (log) => {
    if (!isAdmin && log.user_id !== currentUserId) {
      toast.error('You can only edit your own daily logs.');
      return;
    }

    setEditingLog(log);
    setViewingLog(null);
    setIsViewMode(false);
    setShowFormModal(true);
  };

  const handleViewLog = (log) => {
    setViewingLog(log);
    setEditingLog(null);
    setIsViewMode(true);
    setShowFormModal(true);
  };

  const handleSaveLog = async (logData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to save logs');
        return;
      }

      if (editingLog) {
        // Update existing log
        let updateQuery = supabase
          .from('daily_logs')
          .update({
            focus: logData.todays_focus,
            work_done: logData.work_done,
            blockers: logData.blockers,
            next_steps: logData.next_steps
          })
          .eq('id', editingLog.id);

        if (!isAdmin) {
          updateQuery = updateQuery.eq('user_id', user.id);
        }

        const { data, error } = await updateQuery
          .select('id, log_date, focus, work_done, blockers, next_steps, user_id, users(name)');

        if (error) throw error;

        if (!data || data.length === 0) {
          toast.error('You are not allowed to edit this log.');
          return;
        }

        toast.success('Log updated successfully!');
      } else {
        // Create new log - check for duplicates first
        const logDate = logData.date || selectedDate;
        
        // Check if a log already exists for this user and date
        const { data: existingLog, error: checkError } = await supabase
          .from('daily_logs')
          .select('id')
          .eq('user_id', user.id)
          .eq('log_date', logDate)
          .maybeSingle();

        if (checkError && checkError.code !== 'PGRST116') {
          // PGRST116 is "no rows returned" - that's fine
          throw checkError;
        }

        if (existingLog) {
          toast.error('You have already submitted a log for this date. Please edit the existing one instead.');
          return; // Don't close modal, let user fix the date
        }

        // Insert new log
        const { data, error } = await supabase
          .from('daily_logs')
          .insert([{
            user_id: user.id,
            log_date: logDate,
            focus: logData.todays_focus,
            work_done: logData.work_done,
            blockers: logData.blockers,
            next_steps: logData.next_steps
          }])
          .select('id, log_date, focus, work_done, blockers, next_steps, user_id, users(name)');

        if (error) {
          // Check for unique constraint violation (Postgres error code 23505)
          if (error.code === '23505') {
            toast.error('You have already submitted a log for this date. Please edit the existing one instead.');
            return; // Don't close modal, let user fix the date
          }
          throw error;
        }

        toast.success('Log created successfully!');
      }

      setShowFormModal(false);
      setEditingLog(null);
      setViewingLog(null);
      setIsViewMode(false);
      await loadLogs();
    } catch (error) {
      console.error('Error saving log:', error);
      
      // Check for unique constraint violation (Postgres error code 23505)
      if (error.code === '23505') {
        toast.error('You have already submitted a log for this date. Please edit the existing one instead.');
        // Don't close modal on duplicate error
        return;
      }
      
      toast.error('Failed to save log: ' + error.message);
      // Don't close modal on other errors either, so user can fix issues
    }
  };

  const handleOpenMyDrive = () => {
    const driveLink = userProfile?.drive_folder_link;
    if (driveLink && driveLink.trim() !== '') {
      window.open(driveLink, '_blank');
    } else {
      toast.error('Drive folder link not found');
    }
  };

  const formatDateDisplay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="loading-dots mb-4">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p className="text-gray-400">Loading logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8" data-testid="logger-page">
      {/* Filter Bar */}
      <div className="glass-card p-4 sm:p-6 mb-6">
        <div className="flex flex-col gap-4">
          {/* View Mode Toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <span className="text-xs sm:text-sm font-medium text-gray-300">View Mode:</span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setViewMode('specific_date')}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  viewMode === 'specific_date'
                    ? 'bg-white text-black'
                    : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                }`}
                data-testid="view-mode-specific-date"
              >
                Specific Date
              </button>
              <button
                onClick={() => setViewMode('all_history')}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  viewMode === 'all_history'
                    ? 'bg-white text-black'
                    : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                }`}
                data-testid="view-mode-all-history"
              >
                All History
              </button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* Date Picker - Only show if viewMode is 'specific_date' */}
            {viewMode === 'specific_date' && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="text-gray-400" size={18} />
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="glass-input px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm"
                    data-testid="date-picker"
                  />
                </div>
                <div className="text-gray-400 text-xs sm:text-sm">
                  {formatDateDisplay(selectedDate)}
                </div>
              </div>
            )}

            {/* User Dropdown - Admin Only */}
            {isAdmin && (
              <div className="flex items-center gap-2">
                <Users className="text-gray-400" size={18} />
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="glass-input px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg min-w-[170px] sm:min-w-[200px] text-white text-sm"
                  data-testid="user-filter-dropdown"
                >
                  <option value="ALL" className="bg-gray-800 text-white">All Users</option>
                  {usersList.map((user) => {
                    const isInactive = user.is_active === false;
                    const realName = user.name || user.email;
                    const displayName = isInactive && user.inactive_user_name 
                      ? user.inactive_user_name 
                      : realName;
                    return (
                      <option 
                        key={user.id} 
                        value={user.id}
                        className={isInactive ? 'bg-gray-800 text-gray-400' : 'bg-gray-800 text-white'}
                      >
                        {displayName}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            {/* Admin Drive Links Dropdown */}
            {isAdmin && usersList.filter(user => user.drive_folder_link && user.drive_folder_link.trim() !== '').length > 0 && (
              <div className="flex items-center gap-2">
                <FolderOpen className="text-gray-400" size={18} />
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      window.open(e.target.value, '_blank');
                      e.target.value = ''; // Reset dropdown
                    }
                  }}
                  className="glass-input px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg min-w-[170px] sm:min-w-[200px] text-white text-sm"
                  defaultValue=""
                >
                  <option value="" className="bg-gray-800 text-white">Open User Drive...</option>
                  {usersList
                    .filter(user => user.drive_folder_link && user.drive_folder_link.trim() !== '')
                    .map((user) => {
                      const isInactive = user.is_active === false;
                      const realName = user.name || user.email;
                      const displayName = isInactive && user.inactive_user_name 
                        ? user.inactive_user_name 
                        : realName;
                      return (
                        <option 
                          key={user.id} 
                          value={user.drive_folder_link}
                          className="bg-gray-800 text-white"
                        >
                          {displayName}
                        </option>
                      );
                    })}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Header with Action Buttons */}
      <div className="glass-card p-4 sm:p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-gray-400 text-xs sm:text-sm">
            {viewMode === 'all_history' 
              ? 'Showing all logs' + (selectedUserId !== 'ALL' && isAdmin ? ` for selected user` : '')
              : `Logs for ${formatDateDisplay(selectedDate)}`
            }
          </div>

          {/* Right side: Buttons */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {userProfile?.drive_folder_link && userProfile.drive_folder_link.trim() !== '' && (
              <button
                onClick={handleOpenMyDrive}
                className="btn-primary flex items-center gap-1.5 sm:gap-2 bg-green-600 hover:bg-green-500 text-xs sm:text-sm px-3 sm:px-4 py-2"
                data-testid="my-drive-button"
              >
                <FolderOpen size={16} />
                <span className="hidden sm:inline">My Drive Folder</span>
                <span className="sm:hidden">My Drive</span>
              </button>
            )}
            <button
              onClick={handleCreateLog}
              className="btn-primary flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm px-3 sm:px-4 py-2"
              data-testid="create-log-button"
            >
              <Plus size={16} />
              Create Log
            </button>
          </div>
        </div>
      </div>

      {/* Grid of Log Cards */}
      {logs.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <p className="text-gray-400 text-lg mb-4">
            {viewMode === 'all_history' 
              ? 'No logs found' + (selectedUserId !== 'ALL' && isAdmin ? ' for selected user' : '')
              : `No logs found for ${formatDateDisplay(selectedDate)}`
            }
          </p>
          <button
            onClick={handleCreateLog}
            className="btn-primary"
          >
            Create First Log
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {logs.map((log) => (
            <LogCard
              key={log.id}
              log={log}
              onEdit={() => handleEditLog(log)}
              onView={() => handleViewLog(log)}
              canEdit={isAdmin || log.user_id === currentUserId}
            />
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <LogFormModal
          log={isViewMode ? viewingLog : editingLog}
          selectedDate={selectedDate}
          currentUserId={currentUserId}
          userDriveLink={userProfile?.drive_folder_link}
          readOnly={isViewMode}
          onClose={() => {
            setShowFormModal(false);
            setEditingLog(null);
            setViewingLog(null);
            setIsViewMode(false);
          }}
          onSubmit={handleSaveLog}
        />
      )}
    </div>
  );
};

export default Logger;

